import { LoadingSpinner, Toast, useToast } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import EditableSection from "~/adminApp/pages/EmploymentPage/TabShared/EditableSection"
import { countryCodeToCurrencyCode } from "@oysterhr/common"
import { EquityDetailsDisplay } from "./EquityDetailsDisplay"
import { EquityInlineForm } from "./EquityInlineForm/EquityInlineForm"
import { EquityInlineFormValues } from "./EquityInlineForm/types"
import { useCreateEquityCompanyPlan, useUpdateEquityCompanyPlan } from "./gql/mutations"
import { PresetVestingScheduleDetailsDisplay } from "./PresetVestingScheduleDetailsDisplay"
import { PresetVestingScheduleInlineForm } from "./PresetVestingScheduleInlineForm/PresetVestingScheduleInlineForm"
import { PresetVestingScheduleInlineFormValues } from "./PresetVestingScheduleInlineForm/types"
import { EquityTabProps, HandleSubmitEquityCompanyPlanProps } from "./types"
import EquityDocuments from "./EquityDocuments"
import { useCreateCompanyValuation } from "../../hooks/mutations/useCreateCompanyValuation"
import { gql, useQuery } from "@apollo/client"
import {
  Company,
  Document,
  EquityCompanyPlan,
  EquityCompanyValuation,
  Maybe,
} from "@oysterhr/schema"
import {
  buildRefetchOperation,
  unstable_createRefetchManager,
} from "~/shared/contexts/RefetchManager/RefetchManager"
import { GENERAL_STAFF_CREATE_DOCUMENT } from "~/adminApp/mutations/useCreateDocument"
import { GENERAL_STAFF_DESTROY_DOCUMENT } from "~/adminApp/mutations/useDestroyDocument"

const DOCUMENT_FRAGMENT = gql`
  fragment GeneralStaffCustomerEquityTab_document on Document {
    id
    title
    description
    date
    tags
    filename
    createdAt
  }
`

const EQUITY_COMPANY_PLAN_FRAGMENT = gql`
  fragment GeneralStaffCustomerEquityTab_equityCompanyPlan on EquityCompanyPlan {
    id
    grantorName
    company {
      name
    }
    planName
    contactPersonName
    contactPersonEmail
    contactPersonJobTitle
    vestingScheduleMonths
    vestingCliffMonths
    vestingCliffShares
    documents {
      ...GeneralStaffCustomerEquityTab_document
    }
  }
  ${DOCUMENT_FRAGMENT}
`

const EQUITY_COMPANY_VALUATION_FRAGMENT = gql`
  fragment GeneralStaffCustomerEquityTab_equityCompanyValuation on EquityCompanyValuation {
    id
    valuation {
      subunit
      subunitToUnit
      currencyCode
    }
    effectiveDate
    documents {
      ...GeneralStaffCustomerEquityTab_document
    }
  }
  ${DOCUMENT_FRAGMENT}
`

export const GENERAL_STAFF_GET_CUSTOMER_EQUITY_TAB = gql`
  query GeneralStaffGetCustomerEquityTab($companyId: ID!) {
    company(companyId: $companyId) {
      id
      countryCode
      equityCompanyPlan {
        ...GeneralStaffCustomerEquityTab_equityCompanyPlan
      }
      equityCompanyValuation {
        ...GeneralStaffCustomerEquityTab_equityCompanyValuation
      }
    }
  }

  ${EQUITY_COMPANY_PLAN_FRAGMENT}
  ${EQUITY_COMPANY_VALUATION_FRAGMENT}
`

export type GeneralStaffGetCustomerEquityTab = {
  company?: Maybe<{
    id: Company["id"]
    countryCode?: Company["countryCode"]
    equityCompanyPlan?: Maybe<GeneralStaffCustomerEquityTab_equityCompanyPlan>
    equityCompanyValuation?: Maybe<GeneralStaffCustomerEquityTab_equityCompanyValuation>
  }>
}

export const EquityTab = ({ companyId }: EquityTabProps) => {
  const { data, loading } = useQuery<GeneralStaffGetCustomerEquityTab>(
    GENERAL_STAFF_GET_CUSTOMER_EQUITY_TAB,
    { variables: { companyId } },
  )
  const company = data?.company

  const { t } = useTranslation("company_interface/equity_tab/EquityTab")
  const toast = useToast()
  const [createEquityCompanyPlan] = useCreateEquityCompanyPlan()
  const [updateEquityCompanyPlan] = useUpdateEquityCompanyPlan()

  const [createCompanyValuation] = useCreateCompanyValuation({
    onCompleted: data => {
      if (data?.equityCreateCompanyValuation?.errors?.length > 0) {
        toast({
          render: (
            <Toast variation="error">
              <Toast.Content>{data.equityCreateCompanyValuation.errors.join("\n")}</Toast.Content>
            </Toast>
          ),
        })
      }
    },
  })

  if (loading || !company) {
    return (
      <div className="mt-4 flex justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  const handleSubmitEquityCompanyPlan = async ({
    company,
    variables,
    successMessage,
  }: HandleSubmitEquityCompanyPlanProps) => {
    const isCreate = !company?.equityCompanyPlan?.id
    const response = isCreate
      ? await createEquityCompanyPlan({ variables })
      : await updateEquityCompanyPlan({ variables })

    const { equityCreateCompanyPlan, equityUpdateCompanyPlan } = response.data
    const data = isCreate ? equityCreateCompanyPlan : equityUpdateCompanyPlan
    data?.errors?.length === 0
      ? toast({
          render: (
            <Toast variation="success">
              <Toast.Content>{successMessage}</Toast.Content>
            </Toast>
          ),
        })
      : toast({
          render: (
            <Toast variation="error">
              <Toast.Content>{data.errors.join("\n")}</Toast.Content>
            </Toast>
          ),
        })
  }

  const onSubmitEquitySectionValues = async (
    values: Partial<EquityInlineFormValues>,
    successMessage: string,
  ) => {
    const variables = {
      companyId: company?.id,
      equityPlanId: company?.equityCompanyPlan?.id,
      planName: values.planName || null,
      grantorName: values.grantorName || null,
      contactPersonName: values.contactPersonName || null,
      contactPersonEmail: values.contactPersonEmail || null,
      contactPersonJobTitle: values.contactPersonJobTitle || null,
    }

    if (!company?.equityCompanyPlan?.id) {
      const valuationVariables = {
        companyId: company?.id || "",
        valuation: values.companyValuation?.amount || null,
        valuationCurrency: values.companyValuation?.currency || null,
        effectiveDate: values.companyValuationEffectiveDate || null,
      }
      await createCompanyValuation({ variables: valuationVariables })
    }

    await handleSubmitEquityCompanyPlan({ company, variables, successMessage })
  }

  const onSubmitPresetSectionValues = async (
    values: Partial<PresetVestingScheduleInlineFormValues>,
    successMessage: string,
  ) => {
    const variables = {
      companyId: company?.id,
      equityPlanId: company?.equityCompanyPlan?.id,
      vestingScheduleMonths: values.vestingScheduleMonths ? +values.vestingScheduleMonths : null,
      vestingCliffMonths: values.vestingCliffMonths ? +values.vestingCliffMonths : null,
      vestingCliffShares: values.vestingCliffShares ? +values.vestingCliffShares : null,
    }

    await handleSubmitEquityCompanyPlan({ company, variables, successMessage })
  }

  return (
    <div data-testid="information-details">
      <EditableSection title={t("section.equity.title")} isEditable dataTestId="equity-info">
        {({ isEditing, setEditing }) =>
          !isEditing ? (
            <EquityDetailsDisplay
              equityCompanyPlan={company?.equityCompanyPlan}
              equityCompanyValuation={company?.equityCompanyValuation}
            />
          ) : (
            <EquityInlineForm
              setEditing={setEditing}
              onSubmit={onSubmitEquitySectionValues}
              currencyCode={countryCodeToCurrencyCode(company?.countryCode || "")}
              equityCompanyPlan={company?.equityCompanyPlan}
            />
          )
        }
      </EditableSection>
      <EditableSection
        title={t("section.presetVestingSchedule.title")}
        isEditable
        dataTestId="preset-vesting-schedule-info"
      >
        {({ isEditing, setEditing }) =>
          !isEditing ? (
            <PresetVestingScheduleDetailsDisplay equityCompanyPlan={company?.equityCompanyPlan} />
          ) : (
            <PresetVestingScheduleInlineForm
              setEditing={setEditing}
              onSubmit={onSubmitPresetSectionValues}
              equityCompanyPlan={company?.equityCompanyPlan}
            />
          )
        }
      </EditableSection>
      {company?.equityCompanyPlan && (
        <RefetchManager>
          <EquityDocuments company={company} />
        </RefetchManager>
      )}
    </div>
  )
}

const RefetchManager = unstable_createRefetchManager([
  buildRefetchOperation(GENERAL_STAFF_CREATE_DOCUMENT, [GENERAL_STAFF_GET_CUSTOMER_EQUITY_TAB]),
  buildRefetchOperation(GENERAL_STAFF_DESTROY_DOCUMENT, [GENERAL_STAFF_GET_CUSTOMER_EQUITY_TAB]),
])

export type GeneralStaffCustomerEquityTab_equityCompanyPlan = Pick<
  EquityCompanyPlan,
  | "id"
  | "grantorName"
  | "planName"
  | "contactPersonName"
  | "contactPersonEmail"
  | "contactPersonJobTitle"
  | "vestingScheduleMonths"
  | "vestingCliffMonths"
  | "vestingCliffShares"
> & {
  company?: Maybe<Pick<Company, "name">>
  documents?: Maybe<Array<GeneralStaffCustomerEquityTab_document>>
}

export type GeneralStaffCustomerEquityTab_equityCompanyValuation = Pick<
  EquityCompanyValuation,
  "id" | "effectiveDate"
> & {
  valuation: Pick<EquityCompanyValuation["valuation"], "subunit" | "subunitToUnit" | "currencyCode">
  documents?: Maybe<Array<GeneralStaffCustomerEquityTab_document>>
}

export type GeneralStaffCustomerEquityTab_document = Pick<
  Document,
  "id" | "title" | "description" | "date" | "tags" | "filename" | "createdAt"
>
